import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import { getThemeColors, THEME_OPTIONS } from '../theme'
import Planes from './Planes'

const MAX_KEYWORDS = 5

const MONEDAS = [
  { value: 'MXN', label: 'MXN — Peso mexicano' },
  { value: 'USD', label: 'USD — Dólar americano' },
  { value: 'COP', label: 'COP — Peso colombiano' },
  { value: 'ARS', label: 'ARS — Peso argentino' },
  { value: 'CLP', label: 'CLP — Peso chileno' },
  { value: 'PEN', label: 'PEN — Sol peruano' },
  { value: 'BRL', label: 'BRL — Real brasileño' },
  { value: 'EUR', label: 'EUR — Euro' },
]

export default function Configuracion({ session, theme = THEME_OPTIONS.LIGHT, themeMode = THEME_OPTIONS.SYSTEM, setThemeMode = () => {} }) {
  const C = getThemeColors(theme)
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [suggesting, setSuggesting] = useState(false)
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')
  const [keywordInput, setKeywordInput] = useState('')
  const [keywords, setKeywords] = useState([])
  const [cvText, setCvText] = useState('')
  const [perfil, setPerfil] = useState(null)
  const [showUpgrade, setShowUpgrade] = useState(false)
  const [showPlansModal, setShowPlansModal] = useState(false)
  const [waitlistDone, setWaitlistDone] = useState(false)
  const [salario, setSalario] = useState({ min: '', max: '', moneda: 'MXN' })

  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) { navigate('/login'); return }

      const { data: profile } = await supabase.from('profiles').select('nombre').eq('id', user.id).single()
      setPerfil(profile)

      const { data: sp } = await supabase.from('search_profiles')
        .select('keywords, cv_texto, sectores, ubicacion, seniority, salario_min, salario_max, moneda')
        .eq('user_id', user.id)
        .single()

      if (sp) {
        setKeywords(sp.keywords || [])
        setCvText(sp.cv_texto || '')
        setSalario({ min: sp.salario_min || '', max: sp.salario_max || '', moneda: sp.moneda || 'MXN' })
      }
      setLoading(false)
    }

    loadData()
  }, [navigate])

  const handleSuggest = async () => {
    setSuggesting(true); setError('')
    try {
      const { data, error: fnError } = await supabase.functions.invoke('extract-keywords', {
        body: { cvText: cvText.slice(0, 4000), nombre: perfil?.nombre, plan: 'free' },
      })
      if (fnError) throw fnError
      if (data?.keywords) setKeywords(data.keywords.slice(0, MAX_KEYWORDS))
    } catch (err) { setError('No se pudieron sugerir keywords. Intenta de nuevo.') }
    setSuggesting(false)
  }

  const addKeyword = () => {
    const kw = keywordInput.trim()
    if (!kw || keywords.length >= MAX_KEYWORDS || keywords.includes(kw)) return
    setKeywords([...keywords, kw])
    setKeywordInput('')
  }

  const removeKeyword = (kw) => setKeywords(keywords.filter(k => k !== kw))

  const handleSave = async () => {
    if (keywords.length === 0) { setError('Agrega al menos una keyword.'); return }
    setSaving(true); setError(''); setSuccess('')
    try {
      const { data: { user } } = await supabase.auth.getUser()
      const { error: spError } = await supabase.from('search_profiles').upsert({
        user_id:     user.id,
        keywords,
        salario_min: salario.min ? parseInt(salario.min) : null,
        salario_max: salario.max ? parseInt(salario.max) : null,
        moneda:      salario.moneda,
        updated_at:  new Date().toISOString(),
      }, { onConflict: 'user_id' })
      if (spError) throw spError
      setSuccess('Configuración guardada. Se usará en la próxima búsqueda automática.')
    } catch (err) { setError('Error al guardar. Intenta de nuevo.') }
    setSaving(false)
  }

  const inputStyle = { width: '100%', padding: '10px 14px', background: theme === THEME_OPTIONS.DARK ? '#0f172a' : C.bg, border: `0.5px solid ${C.border}`, borderRadius: '8px', color: C.text, fontSize: '14px', outline: 'none', boxSizing: 'border-box' }
  const selectStyle = { ...inputStyle, background: C.white }
  const labelStyle = { display: 'block', fontSize: '12px', color: C.muted, marginBottom: '6px', marginTop: '14px' }

  if (loading) return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <div style={{ color: C.muted, fontSize: '14px' }}>Cargando...</div>
    </div>
  )

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: "'DM Sans', system-ui, sans-serif", color: C.text }}>
      {showPlansModal && (
        <Planes theme={theme} session={session} isModal onClose={() => setShowPlansModal(false)} />
      )}

      {/* Modal Premium */}
      {showUpgrade && (
        <div onClick={() => { setShowUpgrade(false); setWaitlistDone(false) }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: C.white, borderRadius: '16px', padding: '2rem', width: '100%', maxWidth: '420px', boxSizing: 'border-box' }}>
            <div style={{ fontSize: '20px', fontWeight: '500', color: C.text, marginBottom: '4px' }}>Plan Premium</div>
            <div style={{ fontSize: '13px', color: C.muted, marginBottom: '20px' }}>Próximamente. Únete a la lista para ser el primero en acceder.</div>
            {[['Keywords ilimitadas', '3 en plan gratuito'], ['Vacantes ilimitadas', '5 por día en plan gratuito'], ['Alertas Telegram', 'Solo en Premium'], ['Scoring completo detallado', 'Básico en plan gratuito'], ['Pipeline kanban', 'Solo en Premium']].map(([feat, note]) => (
              <div key={feat} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: `0.5px solid ${C.border}`, fontSize: '13px' }}>
                <span style={{ color: C.text, fontWeight: '500' }}>✓ {feat}</span>
                <span style={{ color: C.subtle }}>{note}</span>
              </div>
            ))}
            <div style={{ fontSize: '28px', fontWeight: '500', color: C.blue, margin: '20px 0 16px' }}>
              $14.99<span style={{ fontSize: '14px', color: C.subtle, fontWeight: '400' }}>/mes</span>
            </div>
            {!waitlistDone ? (
              <button onClick={async () => {
                const { data: { user } } = await supabase.auth.getUser()
                await supabase.from('waitlist').upsert({ email: user.email, user_id: user.id }, { onConflict: 'email' })
                setWaitlistDone(true)
              }} style={{ width: '100%', padding: '12px', background: C.blue, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', marginBottom: '8px' }}>
                Unirme a la lista →
              </button>
            ) : (
              <div style={{ background: C.greenBg, border: `0.5px solid ${C.greenBorder}`, borderRadius: '8px', padding: '14px', textAlign: 'center', marginBottom: '8px' }}>
                <div style={{ fontSize: '1.5rem', marginBottom: '4px' }}>🎉</div>
                <div style={{ fontSize: '14px', fontWeight: '500', color: C.green }}>¡Ya estás en la lista!</div>
                <div style={{ fontSize: '12px', color: C.muted, marginTop: '4px' }}>Te avisamos cuando lancemos Premium.</div>
              </div>
            )}
            <button onClick={() => { setShowUpgrade(false); setWaitlistDone(false) }} style={{ width: '100%', padding: '10px', background: 'none', border: 'none', color: C.subtle, fontSize: '13px', cursor: 'pointer' }}>Cerrar</button>
          </div>
        </div>
      )}

      {/* Nav */}
      <nav style={{ background: C.white, borderBottom: `0.5px solid ${C.border}`, padding: '12px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ fontSize: '15px', fontWeight: '500', color: C.text }}>Job<span style={{ color: C.blue }}>Pipeline</span></div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => navigate('/faqs')} style={{ fontSize: '12px', color: C.muted, background: C.bg, border: `0.5px solid ${C.border}`, borderRadius: '6px', padding: '6px 14px', cursor: 'pointer' }}>FAQs</button>
          <button onClick={() => navigate('/privacidad')} style={{ fontSize: '12px', color: C.muted, background: C.bg, border: `0.5px solid ${C.border}`, borderRadius: '6px', padding: '6px 14px', cursor: 'pointer' }}>Privacidad</button>
          <button onClick={() => navigate('/terminos')} style={{ fontSize: '12px', color: C.muted, background: C.bg, border: `0.5px solid ${C.border}`, borderRadius: '6px', padding: '6px 14px', cursor: 'pointer' }}>Términos</button>
          <button onClick={() => navigate('/dashboard')} style={{ fontSize: '12px', color: C.blue, background: C.blueLight, border: `0.5px solid #BFDBFE`, borderRadius: '6px', padding: '6px 14px', cursor: 'pointer' }}>
          ← Volver al Dashboard
        </button>
        </div>
      </nav>

      <div style={{ maxWidth: '640px', margin: '0 auto', padding: '32px', boxSizing: 'border-box' }}>

        <div style={{ marginBottom: '28px' }}>
          <div style={{ fontSize: '22px', fontWeight: '500', color: C.text, letterSpacing: '-0.5px', marginBottom: '4px' }}>Configuración</div>
          <div style={{ fontSize: '13px', color: C.subtle }}>Personaliza tu búsqueda automática de vacantes</div>
        </div>

        <div style={{ background: C.white, border: `0.5px solid ${C.border}`, borderRadius: '12px', padding: '24px', marginBottom: '16px', boxShadow: C.shadow }}>
          <div style={{ fontSize: '16px', fontWeight: '500', color: C.text }}>Modo de visualizacion</div>
          <div style={{ fontSize: '12px', color: C.subtle, marginTop: '4px', marginBottom: '14px', lineHeight: '1.6' }}>
            Elige como quieres ver JobPipeline en todo el producto.
          </div>
          <div style={{ display: 'grid', gap: '10px' }}>
            {[
              { id: THEME_OPTIONS.SYSTEM, title: 'Automatico', desc: 'Sigue la configuracion de tu sistema', short: 'A' },
              { id: THEME_OPTIONS.LIGHT, title: 'Claro', desc: 'Fondos claros y alto contraste', short: 'L' },
              { id: THEME_OPTIONS.DARK, title: 'Oscuro', desc: 'Ideal para trabajar de noche', short: 'D' },
            ].map((option) => {
              const active = themeMode === option.id
              return (
                <button
                  key={option.id}
                  onClick={() => setThemeMode(option.id)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                    padding: '14px 16px',
                    borderRadius: '12px',
                    border: `1px solid ${active ? C.blue : C.border}`,
                    background: active ? C.blueLight : C.white,
                    color: C.text,
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '34px', height: '34px', borderRadius: '999px', background: active ? C.white : C.bg, border: `0.5px solid ${active ? C.blue : C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '700', color: active ? C.blue : C.muted }}>
                      {option.short}
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '500', color: active ? C.blue : C.text }}>{option.title}</div>
                      <div style={{ fontSize: '12px', color: C.subtle, marginTop: '2px' }}>{option.desc}</div>
                    </div>
                  </div>
                  <div style={{ fontSize: '16px', color: active ? C.blue : 'transparent', fontWeight: '700' }}>✓</div>
                </button>
              )
            })}
          </div>
        </div>

        {/* Keywords */}
        <div style={{ background: C.white, border: `0.5px solid ${C.border}`, borderRadius: '12px', padding: '24px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
            <div style={{ fontSize: '15px', fontWeight: '500', color: C.text }}>Keywords de búsqueda</div>
            <span style={{ fontSize: '11px', background: C.blueLight, color: C.blue, padding: '2px 8px', borderRadius: '20px', fontWeight: '500' }}>
              Plan gratuito · máx {MAX_KEYWORDS}
            </span>
          </div>
          <div style={{ fontSize: '12px', color: C.subtle, marginBottom: '20px', lineHeight: '1.6' }}>
            Estas keywords definen qué roles buscamos para ti en cada ejecución automática (L/M/V 8am).
          </div>

          {(cvText || perfil?.nombre) && (
            <button onClick={handleSuggest} disabled={suggesting} style={{ width: '100%', padding: '10px', background: suggesting ? C.bg : C.blueLight, color: C.blue, border: `0.5px solid #BFDBFE`, borderRadius: '8px', cursor: suggesting ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '500', marginBottom: '16px' }}>
              {suggesting ? '🤖 Nyx.ia está analizando tu CV...' : '✨ Re-sugerir keywords con Nyx.ia'}
            </button>
          )}

          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <input style={{ ...inputStyle, flex: 1 }} placeholder="ej: Project Manager Senior" value={keywordInput}
              onChange={e => setKeywordInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && addKeyword()}
              disabled={keywords.length >= MAX_KEYWORDS} />
            <button onClick={addKeyword} disabled={keywords.length >= MAX_KEYWORDS || !keywordInput.trim()}
              style={{ padding: '10px 16px', background: keywords.length >= MAX_KEYWORDS ? C.bg : C.blue, color: keywords.length >= MAX_KEYWORDS ? C.subtle : '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '500', whiteSpace: 'nowrap' }}>
              + Agregar
            </button>
          </div>

          <div style={{ minHeight: '72px', background: C.bg, borderRadius: '8px', padding: '12px', marginBottom: '10px' }}>
            {keywords.length === 0 ? (
              <div style={{ fontSize: '12px', color: C.subtle, textAlign: 'center', paddingTop: '12px' }}>Sin keywords — agrega al menos una</div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {keywords.map(kw => (
                  <div key={kw} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: C.blueLight, color: C.blue, border: `0.5px solid #BFDBFE`, borderRadius: '20px', padding: '5px 12px', fontSize: '13px' }}>
                    {kw}
                    <span onClick={() => removeKeyword(kw)} style={{ cursor: 'pointer', color: C.subtle, fontSize: '15px', lineHeight: 1 }}>×</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: '11px', color: keywords.length >= MAX_KEYWORDS ? C.amber : C.subtle }}>
              {keywords.length}/{MAX_KEYWORDS} keywords{keywords.length >= MAX_KEYWORDS ? ' · Actualiza a Premium para más' : ''}
            </div>
            {keywords.length >= MAX_KEYWORDS && (
              <button onClick={() => setShowPlansModal(true)} style={{ fontSize: '11px', color: C.blue, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                Ver planes
              </button>
            )}
          </div>
        </div>

        {/* Expectativa salarial */}
        <div style={{ background: C.white, border: `0.5px solid ${C.border}`, borderRadius: '12px', padding: '24px', marginBottom: '16px' }}>
          <div style={{ fontSize: '15px', fontWeight: '500', color: C.text, marginBottom: '4px' }}>Expectativa salarial</div>
          <div style={{ fontSize: '12px', color: C.subtle, marginBottom: '16px' }}>
            Opcional. Se usa para evaluar match con vacantes que publican salario.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div>
              <label style={labelStyle}>Mínimo esperado</label>
              <input style={inputStyle} type="number" placeholder="ej. 50000" value={salario.min}
                onChange={e => setSalario(s => ({ ...s, min: e.target.value }))} />
            </div>
            <div>
              <label style={labelStyle}>Máximo esperado</label>
              <input style={inputStyle} type="number" placeholder="ej. 80000" value={salario.max}
                onChange={e => setSalario(s => ({ ...s, max: e.target.value }))} />
            </div>
          </div>
          <label style={labelStyle}>Moneda</label>
          <select style={selectStyle} value={salario.moneda} onChange={e => setSalario(s => ({ ...s, moneda: e.target.value }))}>
            {MONEDAS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <div style={{ fontSize: '11px', color: C.subtle, marginTop: '10px' }}>
            Si la vacante no publica salario, igual aparece en tu dashboard.
          </div>
        </div>

        {/* Próxima ejecución */}
        <div style={{ background: C.white, border: `0.5px solid ${C.border}`, borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
          <div style={{ fontSize: '15px', fontWeight: '500', color: C.text, marginBottom: '12px' }}>Próxima búsqueda automática</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
            {['Lunes', 'Miércoles', 'Viernes'].map(dia => (
              <div key={dia} style={{ background: C.blueLight, borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                <div style={{ fontSize: '13px', fontWeight: '500', color: C.blue }}>{dia}</div>
                <div style={{ fontSize: '11px', color: '#3b82f6', marginTop: '2px' }}>8:00 AM</div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: '12px', color: C.subtle, marginTop: '12px' }}>
            La búsqueda se ejecuta automáticamente. Los resultados aparecen en tu Dashboard.
          </div>
        </div>

        {/* Fuentes (próximamente) */}
        <div style={{ background: C.white, border: `0.5px solid ${C.border}`, borderRadius: '12px', padding: '20px', marginBottom: '24px', opacity: 0.6 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div style={{ fontSize: '15px', fontWeight: '500', color: C.text }}>Fuentes de búsqueda</div>
            <span style={{ fontSize: '11px', background: C.amberBg, color: C.amber, padding: '2px 8px', borderRadius: '20px', fontWeight: '500' }}>Próximamente</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {['Indeed', 'LinkedIn', 'Computrabajo', 'OCC', 'Bumeran', 'Adzuna'].map(src => (
              <div key={src} style={{ background: C.bg, border: `0.5px solid ${C.border}`, borderRadius: '6px', padding: '5px 12px', fontSize: '12px', color: C.muted }}>{src}</div>
            ))}
          </div>
        </div>

        {error && <div style={{ background: C.redBg, border: `0.5px solid #FECACA`, color: C.red, padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '12px' }}>{error}</div>}
        {success && <div style={{ background: C.greenBg, border: `0.5px solid ${C.greenBorder}`, color: C.green, padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '12px' }}>{success}</div>}

        <div style={{ display: 'flex', gap: '10px', marginBottom: '24px' }}>
          <button onClick={() => navigate('/dashboard')} style={{ flex: 1, padding: '12px', background: C.white, color: C.muted, border: `0.5px solid ${C.border}`, borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>
            ← Mi panel
          </button>
          <button onClick={() => navigate('/feedback')} style={{ flex: 1, padding: '12px', background: C.bg, color: C.blue, border: `0.5px solid ${C.border}`, borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>
            Feedback
          </button>
          <button onClick={handleSave} disabled={saving} style={{ flex: 2, padding: '12px', background: saving ? C.bg : C.blue, color: saving ? C.subtle : '#fff', border: 'none', borderRadius: '8px', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '500' }}>
            {saving ? 'Guardando...' : 'Guardar cambios'}
          </button>
        </div>

      </div>
    </div>
  )
}
